import { toast } from "sonner"
import {
    useAcceptDispatchOrderMutation,
    useCounterDispatchOrderMutation,
    useGetDispatchOrderByIdQuery,
    useRejectDispatchOrderMutation,
} from "../../../redux/services/orders"
import type { NegotiationRequest } from "../../../components/NegotiationRequest"

export type DispatchStatus =
    | "pending"
    | "counter_sent"
    | "customer_countered"
    | "accepted"
    | "rejected"
    | "cancelled"

// Only the latest offer from each side is stored.
export interface CounterOffer {
    company_offered_price: string | null
    customer_offered_price: string | null
    description: string | null
    proposed_by: "company" | "customer" | null
    updated_at: string | null
}

interface DispatchOrder {
    id: string
    price: string
    status: DispatchStatus | string
    counter_offer: CounterOffer | null
    // Customer tapped "Negotiate" (optionally with their own price).
    negotiation_request?: NegotiationRequest | null
    // Only if the API adds it: the price before negotiation, after `price` becomes the agreed one.
    original_price?: string | null
}

export const isDayToDayOrder = (order?: { order_type?: string }) => order?.order_type === "local_dispatch"

export const formatNaira = (value: number | string | null | undefined) =>
    new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", minimumFractionDigits: 0 }).format(Number(value ?? 0))

// 400 → action out of turn, 422 → validation (field → messages), 404 → not found.
export const getDispatchError = (error: any, fallback: string) => {
    const status: number | undefined = error?.status
    const raw = error?.data?.error?.message ?? error?.data?.message
    let message: string = fallback
    if (typeof raw === "string") message = raw
    else if (raw && typeof raw === "object") {
        const first = Object.values(raw as Record<string, string[] | string>)[0]
        message = (Array.isArray(first) ? first[0] : first) || fallback
    }
    return { status, message }
}

export const useDispatchNegotiation = (orderId: string | undefined, enabled: boolean) => {
    const { data, refetch, isFetching, error: loadError } = useGetDispatchOrderByIdQuery(
        { id: orderId ?? "" },
        { skip: !orderId || !enabled }
    )
    const [acceptOrder, { isLoading: isAccepting }] = useAcceptDispatchOrderMutation()
    const [rejectOrder, { isLoading: isRejecting }] = useRejectDispatchOrderMutation()
    const [counterOrder, { isLoading: isCountering }] = useCounterDispatchOrderMutation()

    // The API docs show both `data.data` and `data.data.order` / `data.order`; accept any of them,
    // and ignore a payload without a status rather than render a broken order.
    const rawOrder = data?.data?.data?.order ?? data?.data?.data ?? data?.data?.order ?? data?.order
    const order: DispatchOrder | undefined = rawOrder?.status ? rawOrder : undefined
    const counterOffer = order?.counter_offer ?? null
    const status = order?.status

    // What the company accepts right now: the customer's counter, their proposed
    // price while a negotiation request is open, else the original price.
    const proposedPrice = status === "pending" ? order?.negotiation_request?.proposed_price : null
    const priceOnTable =
        status === "customer_countered" && counterOffer?.customer_offered_price
            ? counterOffer.customer_offered_price
            : proposedPrice || order?.price

    // On a 400 the customer most likely acted first, so show why and pull the latest state.
    const run = async (action: () => Promise<unknown>, success: string, fallback: string) => {
        if (!orderId) return false
        try {
            await action()
            toast.success(success)
            return true
        } catch (error) {
            const { status: code, message } = getDispatchError(error, fallback)
            toast.error(message)
            if (code === 400) refetch()
            return code === 400
        }
    }

    const accept = () =>
        run(() => acceptOrder({ id: orderId! }).unwrap(), `Order accepted at ${formatNaira(priceOnTable)}`, "Failed to accept order")

    const reject = () =>
        run(() => rejectOrder({ id: orderId! }).unwrap(), "Order rejected", "Failed to reject order")

    // Returns a field error for the modal on 422; closes the modal otherwise.
    const counter = async (offeredPrice: number, description: string | null): Promise<string | null> => {
        if (!orderId) return null
        try {
            await counterOrder({ id: orderId, offered_price: offeredPrice, description }).unwrap()
            toast.success("Counter offer sent to customer")
            return null
        } catch (error) {
            const { status: code, message } = getDispatchError(error, "Failed to send counter offer")
            if (code === 422) return message
            toast.error(message)
            if (code === 400) refetch()
            return null
        }
    }

    // Without this the buttons sit disabled with no explanation.
    // A Laravel "route could not be found" 404 means the endpoint isn't deployed;
    // any other 404 carries the API's own reason (e.g. "Dispatch order not found.").
    const loadErrorMessage = (() => {
        if (!loadError || order) return null
        const { status: code, message } = getDispatchError(loadError, "Couldn't load this order's price offer.")
        if (code === 404 && /route .* could not be found/i.test(message)) {
            return "The server couldn't find this order's price offer (404). The negotiation endpoints may not be live yet."
        }
        return message
    })()

    return {
        order,
        loadErrorMessage,
        isLoadingOffer: isFetching && !order,
        retryLoad: refetch,
        status,
        counterOffer,
        priceOnTable,
        accept,
        reject,
        counter,
        isAccepting,
        isRejecting,
        isCountering,
    }
}
